import React, { useState, useMemo } from 'react';
import { formatTimeWithTimezone } from '../../utils/formatUtils';
import { Button, Input, Select, SelectOption, Badge, Card, Accordion, AccordionSummary, AccordionDetails } from '../ui';
import { ChevronDown, Clock, Pencil } from '../ui/icons';

interface ScheduleFieldProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  helperText?: string;
}

// Predefined schedule options
const PREDEFINED_SCHEDULES = [
  { label: 'Manual Only', value: '', description: 'No automatic scheduling' },
  { label: 'Every 15 Minutes', value: '0/15 * * * ? *', description: 'Run every 15 minutes' },
  { label: 'Every 30 Minutes', value: '0/30 * * * ? *', description: 'Run every 30 minutes' },
  { label: 'Every Hour', value: '0 * * * ? *', description: 'Run every hour at minute 0' },
  { label: 'Every 6 Hours', value: '0 */6 * * ? *', description: 'Run every 6 hours' },
  { label: 'Daily at Midnight', value: '0 0 * * ? *', description: 'Run once per day at 12:00 AM' },
  { label: 'Daily at 6 AM', value: '0 6 * * ? *', description: 'Run once per day at 6:00 AM' },
  { label: 'Daily at 9 AM', value: '0 9 * * ? *', description: 'Run once per day at 9:00 AM' },
  { label: 'Daily at 6 PM', value: '0 18 * * ? *', description: 'Run once per day at 6:00 PM' },
  { label: 'Weekdays at 9 AM', value: '0 9 ? * MON-FRI *', description: 'Run Monday to Friday at 9:00 AM' },
  { label: 'Weekends at 10 AM', value: '0 10 ? * SAT,SUN *', description: 'Run Saturday and Sunday at 10:00 AM' },
  { label: 'Weekly on Monday', value: '0 9 ? * MON *', description: 'Run every Monday at 9:00 AM' },
  { label: 'Monthly on 1st', value: '0 9 1 * ? *', description: 'Run on the 1st of each month at 9:00 AM' },
  { label: 'Custom', value: 'custom', description: 'Define your own schedule' },
];

// Custom schedule builder options
const FREQUENCY_OPTIONS = [
  { value: 'manual', label: 'Manual Only' },
  { value: 'custom_interval', label: 'Custom Interval' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom Expression' },
];

const DAYS_OF_WEEK = [
  { value: 'MON', label: 'Monday' },
  { value: 'TUE', label: 'Tuesday' },
  { value: 'WED', label: 'Wednesday' },
  { value: 'THU', label: 'Thursday' },
  { value: 'FRI', label: 'Friday' },
  { value: 'SAT', label: 'Saturday' },
  { value: 'SUN', label: 'Sunday' },
];

const ScheduleField: React.FC<ScheduleFieldProps> = ({
  value,
  onChange,
  label = 'Schedule',
  helperText = 'Configure when this source should be automatically processed',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const [customFrequency, setCustomFrequency] = useState('daily');
  const [customHour, setCustomHour] = useState('9');
  const [customMinute, setCustomMinute] = useState('0');
  const [customInterval, setCustomInterval] = useState('15');
  const [customDay, setCustomDay] = useState('1');
  const [customWeekDays, setCustomWeekDays] = useState<string[]>(['MON', 'TUE', 'WED', 'THU', 'FRI']);
  const [customExpression, setCustomExpression] = useState('');

  // Find the current predefined schedule
  const currentSchedule = useMemo(() => {
    if (!value) return PREDEFINED_SCHEDULES[0];
    return PREDEFINED_SCHEDULES.find(s => s.value === value) || PREDEFINED_SCHEDULES[PREDEFINED_SCHEDULES.length - 1];
  }, [value]);

  // Generate human-readable description of the current schedule
  const scheduleDescription = useMemo(() => {
    if (!value) return 'Manual processing only';

    try {
      const parts = value.split(' ');
      if (parts.length < 6) return 'Invalid schedule format';

      const [minute, hour, dayOfMonth, , dayOfWeek] = parts;

      // Handle different patterns
      if (minute.startsWith('0/') && hour === '*') {
        const interval = minute.split('/')[1];
        return `Every ${interval} minutes`;
      }
      if (hour === '*' && minute === '0') return 'Every hour';
      if (hour === '*/6' && minute === '0') return 'Every 6 hours';
      if (dayOfWeek === '?' && dayOfMonth === '*') {
        if (hour === '0' && minute === '0') return `Daily at midnight UTC (${formatTimeWithTimezone('0', '0')})`;
        return `Daily at ${formatTimeWithTimezone(hour, minute)}`;
      }
      if (dayOfWeek.includes('-') && dayOfWeek.includes('MON-FRI')) {
        return `Weekdays at ${formatTimeWithTimezone(hour, minute)}`;
      }
      if (dayOfWeek.includes('SAT,SUN')) {
        return `Weekends at ${formatTimeWithTimezone(hour, minute)}`;
      }
      if (dayOfWeek === 'MON' && dayOfMonth === '?') {
        return `Weekly on Monday at ${formatTimeWithTimezone(hour, minute)}`;
      }
      if (dayOfMonth === '1' && dayOfWeek === '?') {
        return `Monthly on the 1st at ${formatTimeWithTimezone(hour, minute)}`;
      }

      return `Custom schedule: ${value}`;
    } catch (error) {
      // Fallback for any parsing errors
      console.warn('Failed to parse schedule description:', error);
      return `Custom schedule: ${value}`;
    }
  }, [value]);

  const handlePredefinedChange = (newValue: string) => {
    if (newValue === 'custom') {
      setShowCustomBuilder(true);
      // Initialize custom builder with current value if it's not a predefined one
      if (value && !PREDEFINED_SCHEDULES.find(s => s.value === value)) {
        setCustomExpression(value);
      }
    } else {
      setShowCustomBuilder(false);
      onChange(newValue);
    }
  };

  const generateCustomSchedule = () => {
    let expression = '';

    switch (customFrequency) {
      case 'manual':
        expression = '';
        break;
      case 'custom_interval':
        expression = `0/${customInterval} * * * ? *`;
        break;
      case 'hourly':
        expression = `${customMinute} * * * ? *`;
        break;
      case 'daily':
        expression = `${customMinute} ${customHour} * * ? *`;
        break;
      case 'weekly':
        expression = `${customMinute} ${customHour} ? * ${customWeekDays.join(',')} *`;
        break;
      case 'monthly':
        expression = `${customMinute} ${customHour} ${customDay} * ? *`;
        break;
      case 'custom':
        expression = customExpression;
        break;
    }

    onChange(expression);
    setShowCustomBuilder(false);
  };

  const handleWeekDayToggle = (day: string) => {
    setCustomWeekDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort((a, b) => DAYS_OF_WEEK.findIndex(d => d.value === a) - DAYS_OF_WEEK.findIndex(d => d.value === b))
    );
  };

  const scheduleOptions: SelectOption[] = PREDEFINED_SCHEDULES.map(s => ({
    value: s.value,
    label: `${s.label} - ${s.description}`
  }));

  return (
    <Card className="border border-gray-300 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary-600" />
          <h3 className="text-base font-medium text-gray-900">
            {label}
          </h3>
          <Badge variant="default" size="sm">
            {scheduleDescription}
          </Badge>
        </div>
        <Button
          size="sm"
          variant={isExpanded ? "primary" : "outline"}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Pencil className="h-4 w-4 mr-2" />
          {isExpanded ? 'Done' : 'Edit Schedule'}
        </Button>
      </div>

      {/* Expanded View - Full editor */}
      {isExpanded && (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            {helperText}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Schedule Builder */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Schedule Configuration
              </h4>

              {/* Predefined Schedule Selection */}
              <div className="mb-4">
                <Select
                  label="Choose Schedule"
                  value={currentSchedule.value}
                  onChange={(e) => handlePredefinedChange(e.target.value)}
                  options={scheduleOptions}
                  fullWidth
                />
              </div>

              {/* Custom Schedule Builder */}
              {showCustomBuilder && (
                <Accordion defaultExpanded className="mb-4">
                  <AccordionSummary expandIcon={<ChevronDown className="h-4 w-4" />}>
                    <span className="text-sm font-medium">Custom Schedule Builder</span>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className="space-y-4">
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 mb-2">Frequency</h5>
                        <div className="space-y-2">
                          {FREQUENCY_OPTIONS.map((option) => (
                            <label key={option.value} className="flex items-center">
                              <input
                                type="radio"
                                value={option.value}
                                checked={customFrequency === option.value}
                                onChange={(e) => setCustomFrequency(e.target.value)}
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-700">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {customFrequency !== 'manual' && customFrequency !== 'custom' && customFrequency !== 'custom_interval' && (
                        <div className="grid grid-cols-2 gap-4">
                          <Input
                            label="Hour (0-23)"
                            type="number"
                            value={customHour}
                            onChange={(e) => setCustomHour((e.target as HTMLInputElement).value)}
                            min={0}
                            max={23}
                            fullWidth
                          />
                          <Input
                            label="Minute (0-59)"
                            type="number"
                            value={customMinute}
                            onChange={(e) => setCustomMinute((e.target as HTMLInputElement).value)}
                            min={0}
                            max={59}
                            fullWidth
                          />
                        </div>
                      )}

                      {customFrequency === 'custom_interval' && (
                        <div>
                          <Input
                            label="Interval (minutes)"
                            type="number"
                            value={customInterval}
                            onChange={(e) => setCustomInterval((e.target as HTMLInputElement).value)}
                            min={1}
                            max={59}
                            fullWidth
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            How often to run (e.g., 15 = every 15 minutes)
                          </p>
                        </div>
                      )}

                      {customFrequency === 'weekly' && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-900 mb-2">Days of Week</h5>
                          <div className="flex flex-wrap gap-2">
                            {DAYS_OF_WEEK.map((day) => (
                              <button
                                key={day.value}
                                onClick={() => handleWeekDayToggle(day.value)}
                                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                  customWeekDays.includes(day.value)
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {day.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {customFrequency === 'monthly' && (
                        <Input
                          label="Day of Month (1-31)"
                          type="number"
                          value={customDay}
                          onChange={(e) => setCustomDay((e.target as HTMLInputElement).value)}
                          min={1}
                          max={31}
                          fullWidth
                        />
                      )}

                      {customFrequency === 'custom' && (
                        <div>
                          <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                            value={customExpression}
                            onChange={(e) => setCustomExpression(e.target.value)}
                            placeholder="e.g., 0 0 * * ? *"
                            rows={2}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Format: minute hour day-of-month month day-of-week year
                          </p>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          onClick={generateCustomSchedule}
                          disabled={customFrequency === 'weekly' && customWeekDays.length === 0}
                          size="sm"
                        >
                          Apply Schedule
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowCustomBuilder(false)}
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Manual Cron Input */}
              <div className="mt-4">
                <h5 className="text-sm font-medium text-gray-900 mb-2">
                  Manual Cron Expression
                </h5>
                <Input
                  value={value}
                  onChange={(e) => onChange((e.target as HTMLInputElement).value)}
                  placeholder="e.g., 0 0 * * ? *"
                  fullWidth
                />
                <p className="text-xs text-gray-500 mt-1">
                  Cron format: minute hour day-of-month month day-of-week year
                </p>
              </div>
            </div>

            {/* Right Column - Human Readable Output */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Schedule Preview
              </h4>

              <div className="p-4 bg-white border border-gray-200 rounded-lg min-h-[200px]">
                <p className="text-sm font-medium text-gray-900 mb-2">
                  What this schedule means:
                </p>
                <Badge variant="default" className="mb-4">
                  {scheduleDescription}
                </Badge>

                {value && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500">
                      Next run times will be calculated by the system based on this expression.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default ScheduleField;
